/**
 * TemplateInfoPanel — read-only informational panel for scaffolder templates.
 *
 * Renders a title, markdown content, and optional links inside a styled panel.
 * Emits no value — purely presentational.
 *
 * Template usage:
 *
 *   _info:
 *     title: ' '
 *     type: string
 *     ui:field: TemplateInfoPanel
 *     ui:options:
 *       title: Before You Request Access        # optional heading
 *       severity: info                          # info (default) | warning
 *       content: |-                             # markdown — supports **bold**, bullets, etc.
 *         Access is governed by the OneAdvanced Access Control Policy.
 *
 *         - Request the least privileged role that meets your need
 *         - **Production access** requires a Jira ticket
 *       links:                                  # optional structured links rendered below content
 *         - title: Access Control Policy
 *           url: https://advancedcsg.atlassian.net/wiki/spaces/...
 */

import { MarkdownContent } from '@backstage/core-components';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Warning as WarningAmberIcon } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles()((theme) => ({
  panel: {
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    borderLeft: '4px solid',
  },
  info: {
    borderColor: theme.palette.info?.main ?? '#0288d1',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(2, 136, 209, 0.08)'
        : 'rgba(2, 136, 209, 0.06)',
  },
  warning: {
    borderColor: theme.palette.warning?.main ?? '#ed6c02',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(237, 108, 2, 0.08)'
        : 'rgba(237, 108, 2, 0.06)',
  },
  icon: {
    flexShrink: 0,
    marginTop: 2,
  },
  infoIcon: {
    color: theme.palette.info?.main ?? '#0288d1',
  },
  warningIcon: {
    color: theme.palette.warning?.main ?? '#ed6c02',
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
  // Restore paragraph spacing inside MarkdownContent — default Backstage styles
  // collapse margins too aggressively inside a panel context.
  markdown: {
    '& p': { margin: `${theme.spacing(2)}px 0` },
    '& p:first-child': { marginTop: 0 },
    '& p:last-child': { marginBottom: 0 },
    '& ul, & ol': { margin: `${theme.spacing(2)}px 0`, paddingLeft: theme.spacing(2.5) },
    '& li': { marginBottom: theme.spacing(1) },
    '& li:last-child': { marginBottom: 0 },
  },
  linksRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
}));

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InfoPanelLink {
  title: string;
  url: string;
}

interface InfoPanelOptions {
  title?: string;
  severity?: 'info' | 'warning';
  content?: string;
  links?: InfoPanelLink[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TemplateInfoPanel = ({
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const { classes } = useStyles();
  const options = (uiSchema?.['ui:options'] ?? {}) as InfoPanelOptions;

  const isWarning = options.severity === 'warning';
  const Icon = isWarning ? WarningAmberIcon : InfoOutlinedIcon;
  const iconClass = isWarning ? classes.warningIcon : classes.infoIcon;
  const panelClass = isWarning ? classes.warning : classes.info;

  const hasContent =
    options.title ||
    options.content ||
    (options.links?.length ?? 0) > 0;

  if (!hasContent) return null;

  return (
    <Box className={`${classes.panel} ${panelClass}`}>
      <Icon className={`${classes.icon} ${iconClass}`} fontSize="small" />
      <Box flex={1}>
        {options.title && (
          <Typography variant="body2" className={classes.title}>
            {options.title}
          </Typography>
        )}
        {options.content && (
          <Box className={classes.markdown}>
            <MarkdownContent content={options.content} linkTarget="_blank" />
          </Box>
        )}
        {options.links && options.links.length > 0 && (
          <Box className={classes.linksRow}>
            {options.links.map((link, i) => (
              <Link
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
              >
                {link.title} ↗
              </Link>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
